import { faDownload, faFileImport, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { TFunction } from 'i18next'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Authorization from '../../Authentication/Authorization'
import DialogContext from '../../Authentication/DialogContext'
import { ConfirmDialogProps } from '../../components/ConfirmDialog'
import DataTable from '../../components/DataTable'
import { EditDialogProps } from '../../components/EditDialog'
import ProtectedButton from '../../components/ProtectedButton'
import Loading from '../../layout/Loading'
import TlsService from '../../service/TlsService'
import { formatShaThumbprintColumn } from '../../utils/DatatableUtil'
import GatewayType from '../GatewayType'

const TrustedTLSIssuer: GatewayType = () => {
  const { t } = useTranslation()
  const [certificates, setCertificates] = useState([] as any[])
  const [loading, setLoading] = useState(true)
  const { editDialogPropsSetter, confirmDialogPropsSetter } = useContext(DialogContext)

  useEffect(() => {
    TlsService.getCertificates(setCertificates).finally(() => setLoading(false))
  }, [])

  const columns = useMemo(() => {
    return [
      {
        text: t('certificates.thumbPrint'),
        dataField: 'thumbPrint',
        formatter: (cell: any) => formatShaThumbprintColumn(cell),
      },
      {
        text: t('certificates.subject'),
        dataField: 'dn',
        sort: true,
      },
      {
        text: t('certificates.issuer'),
        dataField: 'issuerDn',
        sort: true,
      },
      {
        text: t('certificates.notBefore'),
        dataField: 'notBefore',
        formatter: (cell: any) => new Date(cell).toString(),
        sort: true,
      },
      {
        text: t('certificates.notAfter'),
        dataField: 'notAfter',
        formatter: (cell: any) => new Date(cell).toString(),
        sort: true,
      },
      {
        text: t('action'),
        dataField: 'df1',
        isDummyField: true,
        headerAlign: 'center',
        formatter: (cellData: any, row: any) => {
          return (
            <>
              <ProtectedButton
                permissions={Authorization.keystore.trustedTLSIssuer}
                variant='link'
                className='text-decoration-none pr-2'
                tooltip={t('certificates.downloadHeader')}
                onClick={() => downloadCertificate(t, confirmDialogPropsSetter, row)}>
                <FontAwesomeIcon icon={faDownload} />
              </ProtectedButton>
              <ProtectedButton
                permissions={Authorization.keystore.trustedTLSIssuer}
                variant='link'
                className='text-danger text-link text-decoration-none'
                tooltip={t('delete')}
                onClick={() => deleteCertificate(t, confirmDialogPropsSetter, setCertificates, row)}>
                <FontAwesomeIcon icon={faTrashAlt} />
              </ProtectedButton>
            </>
          )
        },
      },
    ]
  }, [t, confirmDialogPropsSetter])
  return (
    <Loading isLoading={loading}>
      <DataTable
        keyField='label'
        data={certificates}
        columns={columns}
        caption={t('certificates.text')}
        captionButtons={[
          {
            onClick: () => importCertificate(t, editDialogPropsSetter, setCertificates),
            text: t('import'),
            icon: faFileImport,
            permissions: Authorization.keystore.trustedTLSIssuer,
          },
        ]}
        helpLink={t('certificates.helpLink')}
      />
    </Loading>
  )
}

function importCertificate(t: TFunction, editDialogPropsSetter: any, setCertificates: any) {
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('certificates.importHeader'),
    onSubmit: TlsService.importCert,
    onSubmitExtraArgs: [setCertificates, t],
    data: [
      {
        id: 'certificate',
        type: 'file',
        label: t('certificate.text'),
      },
    ],
  }

  editDialogPropsSetter(editDialogProps)
}

function downloadCertificate(t: TFunction, confirmDialogPropsSetter: any, row: any) {
  const resetDialogProps: ConfirmDialogProps = {
    show: true,
    header: t('certificates.downloadHeader'),
    body: t('certificates.downloadBody'),
    submitText: t('yes'),
    onSubmit: TlsService.download,
    onSubmitExtraArgs: [row.id, t],
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(resetDialogProps)
}

function deleteCertificate(t: TFunction, confirmDialogPropsSetter: any, setCertificates: Function, row: any) {
  const resetDialogProps: ConfirmDialogProps = {
    show: true,
    header: t('certificates.deleteHeader'),
    body: t('certificates.deleteBody'),
    submitText: t('yes'),
    onSubmit: TlsService.deleteCertificate,
    onSubmitExtraArgs: [row.id, setCertificates, t],
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(resetDialogProps)
}

export default TrustedTLSIssuer
